import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@interacta-shared/data-access-common';
import {
    InteractaBuildInfo,
    isNewBuild,
} from '@interacta-shared/data-access-configuration';
import { ExternalLinksStateService } from '@interacta-shared/feature-external-links';
import { CatalogsStateService } from '@interacta/data-access-catalog';
import { CommunitiesStateService } from '@interacta/data-access-community-tree';
import { CarouselService } from '@modules/carousel/services/carousel.service';
import { FilterCommunitiesService } from '@modules/communities/services/filter-communities.service';
import { GoogleAPIService } from '@modules/core/services/google-api.service';
import { AppActions } from '@modules/core/store';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { GalleryStateService } from './gallery-state.service';
import { IStateService } from './istate-service';
import { UIStateService } from './ui-state.service';

const BUILD_INFO_KEY = 'buildInfo';

@Injectable({ providedIn: 'root' })
export class StateManagerService {
    private communitiesStateService = inject(CommunitiesStateService);
    private filterCommunitiesService = inject(FilterCommunitiesService);
    private localStorageService = inject(LocalStorageService);
    private store = inject(Store);
    private galleryStateService = inject(GalleryStateService);
    private uiStateService = inject(UIStateService);
    private googleAPIService = inject(GoogleAPIService);
    private carouselService = inject(CarouselService);
    private externalLinksStateService = inject(ExternalLinksStateService);
    private catalogsStateService = inject(CatalogsStateService);

    private readonly stateServices: IStateService<any>[] = [
        this.galleryStateService,
        this.uiStateService,
        this.googleAPIService,
        this.carouselService,
        this.externalLinksStateService,
    ];

    initialize(): void {
        for (const state of this.stateServices) {
            state.initialize();
        }
        this.catalogsStateService.initialize();
        this.communitiesStateService.initialize();
    }

    flush(): void {
        for (const state of this.stateServices) {
            state.flush();
        }
        this.catalogsStateService.flush();
        this.communitiesStateService.flush();
        this.filterCommunitiesService.flush();
        this.store.dispatch(AppActions.flush());
    }

    /**
     * Match current build-info (FE and BE ) agaist cached ones.
     * If a new build is detected, version-aware cache data are deleted.
     * Current build-info are then stored in cache.
     *
     * @param currentBuildInfo
     */
    validateCache(currentBuildInfo: InteractaBuildInfo): Observable<void> {
        const cachedBuildInfo: InteractaBuildInfo | undefined =
            this.localStorageService.getEntry<InteractaBuildInfo>(
                BUILD_INFO_KEY,
                false,
            );
        this.localStorageService.setEntry(
            BUILD_INFO_KEY,
            currentBuildInfo,
            false,
        );

        if (cachedBuildInfo && isNewBuild(cachedBuildInfo, currentBuildInfo)) {
            console.log(
                'A new version has been detected: do clear local cache',
            );
            return this.communitiesStateService.clearCommunitiesCache(true);
        } else {
            return of(void 0);
        }
    }
}
